/**
 * Labs list, upload, and the Review & Verify screen, BUILD.md §4, §6, §9.
 *
 * Review & Verify is the physician-in-the-loop checkpoint (§0.6): AI-extracted
 * values arrive unverified and a clinician must approve them before any score is
 * computed. The screen shows a live preview so the effect of an edit is visible
 * before committing.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { ScoreRunResult } from '@mcos/types';
import { api, qs } from '../lib/api';
import { openAuthedFile } from '../components/AuthedImage';
import { useAuth } from '../lib/auth';
import {
  formatDate,
  fullName,
  STATUS_LABEL,
  statusPillClass,
} from '../lib/format';
import {
  DomainScorePills,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  LoadingRows,
  PageHeader,
  Pill,
  ScoreGauge,
  ScorePill,
  Table,
  Td,
  Th,
  Toggle,
  inputClass,
} from '../components/ui';
import { IconAlert, IconCheck, IconUpload } from '../components/icons';

/* ------------------------------------------------------------------ *
 * List + upload
 * ------------------------------------------------------------------ */

interface LabRow {
  id: string;
  collectionDate: string;
  status: string;
  sourceFileName: string | null;
  extractionError: string | null;
  createdAt: string;
  patient: { id: string; firstName: string; lastName: string };
  _count: { values: number };
  scoreRuns: Array<{
    id: string;
    validationStatus: string;
    metabolicScore: number | null;
    inflammationScore: number | null;
    liverScore: number | null;
    confidence: string;
  }>;
}

export function LabsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') ?? '';
  const showUpload = searchParams.get('upload') === '1';

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['labs', statusFilter],
    queryFn: () =>
      api.get<{ data: LabRow[]; total: number }>(
        `/labs${qs({ status: statusFilter, pageSize: 100 })}`,
      ),
  });

  return (
    <>
      <PageHeader
        title="Labs"
        subtitle="Upload documents, review extracted values, and calculate scores."
        actions={
          <Button
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              if (showUpload) next.delete('upload');
              else next.set('upload', '1');
              setSearchParams(next);
            }}
          >
            <IconUpload size={15} />
            Upload lab report
          </Button>
        }
      />

      {showUpload && (
        <UploadForm
          onDone={() => {
            const next = new URLSearchParams(searchParams);
            next.delete('upload');
            setSearchParams(next);
            void refetch();
          }}
        />
      )}

      <Card padded={false}>
        <div className="p-4 border-b border-ink/5 flex flex-wrap gap-2">
          {[
            { value: '', label: 'All' },
            { value: 'UPLOADED', label: 'Uploaded' },
            { value: 'EXTRACTED', label: 'Awaiting review' },
            { value: 'SCORED', label: 'Scored' },
            { value: 'REPORTED', label: 'Completed' },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                if (option.value) next.set('status', option.value);
                else next.delete('status');
                setSearchParams(next);
              }}
              className={`text-xs px-2.5 py-1.5 rounded-sm2 border transition ${
                statusFilter === option.value
                  ? 'border-[var(--brand-accent)] text-[var(--brand-accent)] bg-blue-50/60 font-medium'
                  : 'border-ink/10 text-ink-muted hover:bg-ink/5'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {error ? (
          <div className="p-5"><ErrorState error={error} retry={() => void refetch()} /></div>
        ) : isLoading ? (
          <div className="p-5"><LoadingRows rows={6} /></div>
        ) : !data || data.data.length === 0 ? (
          <EmptyState
            title="No lab reports"
            body="Upload a PDF or image of a lab report to get started."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Patient</Th>
                <Th>Collection date</Th>
                <Th>Document</Th>
                <Th align="center">Values</Th>
                <Th align="center">Status</Th>
                <Th align="right">Scores</Th>
                <Th align="right"> </Th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((row) => (
                <tr key={row.id} className="hover:bg-ink/5/70">
                  <Td className="max-sm:whitespace-nowrap">
                    <Link
                      to={`/patients/${row.patient.id}`}
                      className="font-medium text-ink hover:text-[var(--brand-accent)]"
                    >
                      {fullName(row.patient)}
                    </Link>
                  </Td>
                  <Td className="text-ink-muted max-sm:whitespace-nowrap">{formatDate(row.collectionDate)}</Td>
                  <Td className="text-ink-muted text-xs break-all max-w-[220px]">
                    {row.sourceFileName ?? 'Manual entry'}
                    {row.extractionError && (
                      <span className="block text-red-600 mt-0.5 flex items-center gap-1">
                        <IconAlert size={12} /> extraction failed
                      </span>
                    )}
                  </Td>
                  <Td align="center" className="text-ink-muted">{row._count.values}</Td>
                  <Td align="center">
                    <Pill className={statusPillClass(row.status)}>
                      {STATUS_LABEL[row.status] ?? row.status}
                    </Pill>
                  </Td>
                  <Td align="right">
                    <DomainScorePills
                      metabolic={row.scoreRuns[0]?.metabolicScore}
                      inflammation={row.scoreRuns[0]?.inflammationScore}
                      liver={row.scoreRuns[0]?.liverScore}
                    />
                  </Td>
                  <Td align="right">
                    <Link
                      to={`/labs/${row.id}`}
                      className="text-xs font-medium text-[var(--brand-accent)] hover:underline"
                    >
                      {row.status === 'EXTRACTED' || row.status === 'UPLOADED' ? 'Review' : 'Open'}
                    </Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

function UploadForm({ onDone }: { onDone: () => void }) {
  const [searchParams] = useSearchParams();
  const [patientId, setPatientId] = useState(searchParams.get('patientId') ?? '');
  const [collectionDate, setCollectionDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const { data: patients } = useQuery({
    queryKey: ['patients', 'picker'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; firstName: string; lastName: string }> }>(
        `/patients${qs({ pageSize: 100 })}`,
      ),
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      form.set('patientId', patientId);
      form.set('collectionDate', collectionDate);
      if (file) form.set('file', file);

      const created = await api.post<{ id: string }>('/labs', form as unknown as FormData);

      // Extraction is best-effort: without an LLM configured the clinician can
      // still enter values by hand, so a failure here must not block the upload.
      if (file) {
        try {
          await api.post(`/labs/${created.id}/extract`);
        } catch {
          // Surfaced on the review screen as `extractionError`.
        }
      }
      return created;
    },
    onSuccess: (created) => {
      onDone();
      navigate(`/labs/${created.id}`);
    },
    onError: (caught) => setError(caught instanceof Error ? caught.message : 'Upload failed'),
  });

  return (
    <Card className="mb-6" title="Upload lab report">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          mutation.mutate();
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Patient" required>
            <select
              required
              className={inputClass}
              value={patientId}
              onChange={(event) => setPatientId(event.target.value)}
            >
              <option value="">Select a patient…</option>
              {patients?.data.map((patient) => (
                <option key={patient.id} value={patient.id}>
                  {fullName(patient)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Collection date" required hint="The date the specimen was drawn">
            <input
              type="date"
              required
              // Today in the clinic's own time zone: no blood is drawn in the future, and the API refuses it too.
              max={new Date(Date.now() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 10)}
              className={inputClass}
              value={collectionDate}
              onChange={(event) => setCollectionDate(event.target.value)}
            />
          </Field>
        </div>

        <div
          onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            const dropped = event.dataTransfer.files[0];
            if (dropped) setFile(dropped);
          }}
          className={`rounded-md2 border-2 border-dashed px-6 py-8 text-center transition ${
            dragging ? 'border-[var(--brand-accent)] bg-blue-50/50' : 'border-ink/10'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/png,image/jpeg"
            className="hidden"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
          <p className="text-sm text-ink">
            {file ? (
              <span className="font-medium">{file.name}</span>
            ) : (
              'Drag a lab report here, or'
            )}
          </p>
          <div className="mt-2.5">
            <Button variant="secondary" size="sm" onClick={() => inputRef.current?.click()}>
              {file ? 'Choose a different file' : 'Browse files'}
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-ink-muted">
            PDF, PNG or JPEG, up to 25 MB. You can also skip the file and enter values manually.
          </p>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-2">
          <Button type="submit" loading={mutation.isPending} disabled={!patientId}>
            {file ? 'Upload and extract' : 'Create report'}
          </Button>
          <Button variant="secondary" onClick={onDone}>Cancel</Button>
        </div>
      </form>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Review & Verify
 * ------------------------------------------------------------------ */

interface MarkerCatalogueEntry {
  markerId: string;
  name: string;
  domain: string;
  unit: string;
  type: 'Direct' | 'Derived';
  decimals: number;
  sexSpecific: boolean;
  weightPct: number;
}

interface LabDetail {
  id: string;
  collectionDate: string;
  status: string;
  sourceFileUrl: string | null;
  sourceFileName: string | null;
  extractionModel: string | null;
  extractionError: string | null;
  hsCrpPossiblyConfounded: boolean;
  patient: {
    id: string;
    firstName: string;
    lastName: string;
    sexAtBirth: 'Female' | 'Male' | null;
    dob: string | null;
  };
  values: Array<{
    id: string;
    markerId: string | null;
    rawName: string;
    rawValue: number;
    unit: string;
    source: 'ai' | 'manual';
    confidence: number | null;
    verified: boolean;
    fasting: boolean;
  }>;
  markerCatalogue: MarkerCatalogueEntry[];
}

interface EditableRow {
  markerId: string;
  value: string;
  unit: string;
  fasting: boolean;
  source: 'ai' | 'manual';
  confidence: number | null;
  verified: boolean;
}

export function LabReviewPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { can } = useAuth();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['lab', id],
    queryFn: () => api.get<LabDetail>(`/labs/${id}`),
    enabled: Boolean(id),
  });

  const [rows, setRows] = useState<EditableRow[]>([]);
  const [confounded, setConfounded] = useState(false);
  const [preview, setPreview] = useState<ScoreRunResult | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Seed the editable table once the report loads.
  useEffect(() => {
    if (!data) return;
    setRows(
      data.values
        .filter((value) => value.markerId !== null)
        .map((value) => ({
          markerId: value.markerId!,
          value: String(value.rawValue),
          unit: value.unit,
          fasting: value.fasting,
          source: value.source,
          confidence: value.confidence,
          verified: value.verified,
        })),
    );
    setConfounded(data.hsCrpPossiblyConfounded);
  }, [data]);

  const catalogue = useMemo(() => {
    const map = new Map<string, MarkerCatalogueEntry>();
    for (const entry of data?.markerCatalogue ?? []) map.set(entry.markerId, entry);
    return map;
  }, [data]);

  const unusedMarkers = useMemo(
    () =>
      (data?.markerCatalogue ?? [])
        .filter((entry) => entry.type === 'Direct')
        .filter((entry) => !rows.some((row) => row.markerId === entry.markerId)),
    [data, rows],
  );

  const saveMutation = useMutation({
    mutationFn: () =>
      api.patch(`/labs/${id}/values`, {
        values: rows.map((row) => ({
          markerId: row.markerId,
          rawValue: Number(row.value),
          unit: row.unit,
          fasting: row.fasting,
        })),
        hsCrpPossiblyConfounded: confounded,
      }),
    onSuccess: () => {
      setSaveError(null);
      setMessage('Changes saved.');
      void queryClient.invalidateQueries({ queryKey: ['lab', id] });
    },
    onError: (caught) => setSaveError(caught instanceof Error ? caught.message : 'Could not save'),
  });

  const previewMutation = useMutation({
    mutationFn: async () => {
      await api.patch(`/labs/${id}/values`, {
        values: rows.map((row) => ({
          markerId: row.markerId,
          rawValue: Number(row.value),
          unit: row.unit,
          fasting: row.fasting,
        })),
        hsCrpPossiblyConfounded: confounded,
      });
      return api.post<{ preview: true; run: ScoreRunResult }>(`/labs/${id}/preview`);
    },
    onSuccess: (result) => {
      setPreview(result.run);
      setSaveError(null);
    },
    onError: (caught) => setSaveError(caught instanceof Error ? caught.message : 'Preview failed'),
  });

  const verifyMutation = useMutation({
    mutationFn: async () => {
      await api.patch(`/labs/${id}/values`, {
        values: rows.map((row) => ({
          markerId: row.markerId,
          rawValue: Number(row.value),
          unit: row.unit,
          fasting: row.fasting,
        })),
        hsCrpPossiblyConfounded: confounded,
      });
      return api.post<{ scoreRunId: string; run: ScoreRunResult }>(`/labs/${id}/verify`);
    },
    onSuccess: (result) => {
      setPreview(result.run);
      setSaveError(null);
      void queryClient.invalidateQueries({ queryKey: ['lab', id] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });

      if (result.run.visual_report.allowed) {
        setMessage('Scores approved. You can now generate the report.');
      } else {
        setMessage(null);
      }
    },
    onError: (caught) => setSaveError(caught instanceof Error ? caught.message : 'Verification failed'),
  });

  const generateMutation = useMutation({
    mutationFn: (scoreRunId: string) =>
      api.post<{ reportId: string }>('/reports', { scoreRunId, notify: false }),
    onSuccess: (result) => navigate(`/reports/${result.reportId}`),
    onError: (caught) =>
      setSaveError(caught instanceof Error ? caught.message : 'Could not generate the report'),
  });

  if (error) return <ErrorState error={error} retry={() => void refetch()} />;
  if (isLoading || !data) return <LoadingRows rows={10} />;

  const editable = can('ADMIN', 'CLINICIAN');
  const lastRunId = verifyMutation.data?.scoreRunId;

  return (
    <>
      <PageHeader
        title="Review & Verify"
        subtitle={`${fullName(data.patient)} · collected ${formatDate(data.collectionDate)}`}
        actions={
          <>
            {data.sourceFileUrl && (
              <Button variant="secondary" size="sm" onClick={() => void openAuthedFile(data.sourceFileUrl!)}>View document</Button>
            )}
            <Link to={`/patients/${data.patient.id}`}>
              <Button variant="ghost">Patient</Button>
            </Link>
          </>
        }
      />

      {/* Physician-in-the-loop notice (§0.6). */}
      {!editable && (
        <div className="mb-5 rounded-md2 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Only a clinician or admin can verify values and calculate scores. You can view this
          report but not approve it.
        </div>
      )}

      {data.extractionError &&
        /turned off|not configured/i.test(data.extractionError) ? (
          /*
           * Automatic reading being switched off is not a failure, and showing
           * it in red as one tells a clinic something is broken when nothing
           * is. Manual entry is the normal pathway here, so this is neutral.
           */
          <div className="mb-5 rounded-md2 border border-ink/10 glass-standard px-4 py-3">
            <p className="text-sm font-semibold text-ink">Enter the values manually</p>
            <p className="text-xs text-ink-muted mt-0.5">{data.extractionError}</p>
          </div>
        ) : data.extractionError ? (
          <div className="mb-5 rounded-md2 border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm font-medium text-red-800">Automatic extraction failed</p>
            <p className="text-xs text-red-700 mt-0.5">{data.extractionError}</p>
            <p className="text-xs text-red-700 mt-1">Enter the values manually below.</p>
          </div>
        ) : null}

      {!data.patient.sexAtBirth && (
        <div className="mb-5 rounded-md2 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          This patient has no sex at birth recorded, so HDL, ferritin, ESR, uric acid, ALT and GGT
          cannot be scored (rule MD09). Add it on the patient record first.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-6">
        <div className="space-y-6">
          <Card title="Extracted values" padded={false}>
            {rows.length === 0 ? (
              <EmptyState
                title="No values on this report"
                body="Add markers below to enter results by hand."
              />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Marker</Th>
                    <Th align="right">Value</Th>
                    <Th>Unit</Th>
                    <Th align="center">Source</Th>
                    <Th align="center">Fasting</Th>
                    <Th align="right"> </Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const meta = catalogue.get(row.markerId);
                    const unitMismatch =
                      meta !== undefined &&
                      row.unit.trim().toLowerCase() !== meta.unit.toLowerCase();
                    return (
                      <tr key={row.markerId} className="hover:bg-ink/5/60">
                        <Td>
                          <span className="font-medium text-ink">
                            {meta?.name ?? row.markerId}
                          </span>
                          {meta && (
                            <span className="block text-[11px] text-ink-muted">
                              {meta.domain} · weight {meta.weightPct}%
                              {meta.sexSpecific && ' · sex-specific'}
                            </span>
                          )}
                        </Td>
                        <Td align="right">
                          <input
                            type="number"
                            step="any"
                            disabled={!editable}
                            value={row.value}
                            onChange={(event) => {
                              const next = [...rows];
                              next[index] = { ...row, value: event.target.value };
                              setRows(next);
                            }}
                            className="w-24 text-right rounded border border-ink/10 px-2 py-1 text-sm
                              focus:outline-none focus:ring-2 focus:ring-[var(--brand-accent)] disabled:bg-ink/5"
                          />
                        </Td>
                        <Td>
                          <input
                            type="text"
                            disabled={!editable}
                            value={row.unit}
                            onChange={(event) => {
                              const next = [...rows];
                              next[index] = { ...row, unit: event.target.value };
                              setRows(next);
                            }}
                            className={`w-24 rounded border px-2 py-1 text-sm focus:outline-none focus:ring-2
                              focus:ring-[var(--brand-accent)] disabled:bg-ink/5
                              ${unitMismatch ? 'border-amber-400 bg-amber-50' : 'border-ink/10'}`}
                          />
                          {unitMismatch && (
                            <span className="block text-[10px] text-amber-700 mt-0.5">
                              expected {meta!.unit}
                            </span>
                          )}
                        </Td>
                        <Td align="center">
                          <Pill
                            className={
                              row.source === 'ai'
                                ? 'bg-indigo-50 text-indigo-700 ring-indigo-200'
                                : 'bg-ink/5 text-ink-muted ring-ink/10'
                            }
                          >
                            {row.source === 'ai'
                              ? `AI ${row.confidence !== null ? `${Math.round(row.confidence * 100)}%` : ''}`
                              : 'manual'}
                          </Pill>
                        </Td>
                        <Td align="center">
                          <input
                            type="checkbox"
                            disabled={!editable}
                            checked={row.fasting}
                            aria-label={`${meta?.name ?? row.markerId} fasting`}
                            onChange={(event) => {
                              const next = [...rows];
                              next[index] = { ...row, fasting: event.target.checked };
                              setRows(next);
                            }}
                            className="h-3.5 w-3.5 rounded border-ink/10"
                          />
                        </Td>
                        <Td align="right">
                          {editable && (
                            <button
                              type="button"
                              onClick={() => setRows(rows.filter((_, i) => i !== index))}
                              className="text-[11px] text-ink-muted hover:text-red-600"
                            >
                              Remove
                            </button>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}

            {editable && unusedMarkers.length > 0 && (
              <div className="p-4 border-t border-ink/5">
                <Field label="Add a marker">
                  <select
                    className={inputClass}
                    value=""
                    onChange={(event) => {
                      const markerId = event.target.value;
                      if (!markerId) return;
                      const meta = catalogue.get(markerId);
                      setRows([
                        ...rows,
                        {
                          markerId,
                          value: '',
                          unit: meta?.unit ?? '',
                          fasting: true,
                          source: 'manual',
                          confidence: null,
                          verified: false,
                        },
                      ]);
                    }}
                  >
                    <option value="">Choose a marker…</option>
                    {unusedMarkers.map((marker) => (
                      <option key={marker.markerId} value={marker.markerId}>
                        {marker.name} ({marker.unit})
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            )}
          </Card>

          {editable && (
            <Card title="Clinical notes">
              <Toggle
                checked={confounded}
                onChange={setConfounded}
                label="hs-CRP may be confounded"
                description="Acute illness, injury, vaccination or intense exercise. Scores still calculate, but confidence drops to Limited and the report advises repeating when well (rule MD08)."
              />
            </Card>
          )}

          {(message || saveError) && (
            <div
              className={`rounded-md2 px-4 py-3 text-sm ${
                saveError
                  ? 'border border-red-200 bg-red-50 text-red-800'
                  : 'border border-emerald-200 bg-emerald-50 text-emerald-800'
              }`}
            >
              {saveError ?? message}
            </div>
          )}

          {editable && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                loading={previewMutation.isPending}
                onClick={() => previewMutation.mutate()}
              >
                Preview scores
              </Button>
              <Button
                variant="secondary"
                loading={saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
              >
                Save draft
              </Button>
              <Button
                loading={verifyMutation.isPending}
                onClick={() => verifyMutation.mutate()}
              >
                <IconCheck size={15} />
                Approve &amp; calculate
              </Button>
            </div>
          )}
        </div>

        {/* --- Score panel ------------------------------------------- */}
        <div className="space-y-6">
          <Card title={preview ? 'Scores' : 'Scores (not calculated)'}>
            {!preview ? (
              <p className="text-sm text-ink-muted py-6 text-center">
                Preview or approve to see the calculated scores.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-1">
                  <ScoreGauge score={preview.domains.metabolic.score} label="Metabolic" size={84} />
                  <ScoreGauge score={preview.domains.inflammation.score} label="Inflammation" size={84} />
                  <ScoreGauge score={preview.domains.liver.score} label="Liver" size={84} />
                </div>

                <div className="mt-4 pt-4 border-t border-ink/5 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-ink-muted">Validation</span>
                    <Pill
                      className={
                        preview.validation_status === 'PASS'
                          ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                          : 'bg-red-50 text-red-700 ring-red-200'
                      }
                    >
                      {preview.validation_status}
                    </Pill>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-ink-muted">Confidence</span>
                    <span className="text-ink font-medium">{preview.confidence}</span>
                  </div>
                </div>

                {/* Derived values (§5.3). */}
                <div className="mt-4 pt-4 border-t border-ink/5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted mb-2">
                    Derived
                  </p>
                  <dl className="space-y-1.5 text-xs">
                    {['HOMA_IR', 'TG_HDL_RATIO', 'FIB4', 'BMI'].map((key) => {
                      const derived = preview.derived[key];
                      return (
                        <div key={key} className="flex items-start justify-between gap-2">
                          <dt className="text-ink-muted">{key.replace(/_/g, '-')}</dt>
                          <dd className={derived?.value === null ? 'text-ink-muted text-right' : 'text-ink font-medium'}>
                            {derived?.value ?? 'not computed'}
                          </dd>
                        </div>
                      );
                    })}
                  </dl>
                </div>

                {/* Gate failures block report generation (§5.6 MD05). */}
                {preview.validation_status !== 'PASS' && (
                  <div className="mt-4 rounded-sm2 border border-red-200 bg-red-50 p-3">
                    <p className="text-xs font-semibold text-red-800 flex items-center gap-1.5">
                      <IconAlert size={13} />
                      Report blocked
                    </p>
                    <ul className="mt-1.5 space-y-1 text-[11px] text-red-700 list-disc pl-4">
                      {Object.values(preview.domains)
                        .flatMap((domain) => domain.gate_failures)
                        .map((failure, index) => (
                          <li key={index}>{failure}</li>
                        ))}
                    </ul>
                  </div>
                )}

                {preview.visual_report.allowed && lastRunId && (
                  <div className="mt-4">
                    <Button
                      className="w-full"
                      loading={generateMutation.isPending}
                      onClick={() => generateMutation.mutate(lastRunId)}
                    >
                      Generate report
                    </Button>
                  </div>
                )}
              </>
            )}
          </Card>

          {preview && preview.audit.missing.length > 0 && (
            <Card title="Missing markers">
              <ul className="space-y-2 text-xs">
                {preview.audit.missing.slice(0, 12).map((entry) => (
                  <li key={entry.marker_id}>
                    <span className="font-medium text-ink">{entry.marker_name}</span>
                    <span className="ml-1.5 text-ink-muted">{entry.rule}</span>
                    <p className="text-ink-muted mt-0.5">{entry.reason}</p>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {preview && preview.audit.flags.some((flag) => flag.severity !== 'info') && (
            <Card title="Flags">
              <ul className="space-y-2 text-xs">
                {preview.audit.flags
                  .filter((flag) => flag.severity !== 'info')
                  .map((flag, index) => (
                    <li key={index} className="flex gap-2">
                      <span
                        className={
                          flag.severity === 'error' ? 'text-red-500' : 'text-amber-500'
                        }
                      >
                        <IconAlert size={13} />
                      </span>
                      <span className="text-ink-muted">{flag.message}</span>
                    </li>
                  ))}
              </ul>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
